import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Platform,
  TouchableOpacity,
  Pressable,
  Image,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';
import { useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { RootStackParamList, ClothingItem } from '../types';
import {
  AppHeader,
  ConfirmModal,
  ScreenLayout,
  ItemCard,
  ClubCard,
  EditProfileForm,
  ChangePasswordForm,
  DeleteAccountForm,
  SectionHeader,
  EmptyState,
  Button,
  PageTitle,
} from '../components';
import { useResponsive } from '../utils/responsive';
import { Colors, Fonts, Radii, Shadows, Spacing, Typography, LAYOUT } from '../constants';
import { authService } from '../services/auth';
import { itemsService } from '../services/items';
import { imageService } from '../services/images';
import { salesService } from '../services/sales';
import { useUser, useUserClubs } from '../contexts';

type Props = NativeStackScreenProps<RootStackParamList, 'Profile'>;

type ActivePanel = 'none' | 'editProfile' | 'changePassword' | 'deleteAccount';

/** Settings row on the profile card: icon, label, optional value, chevron */
const ProfileRow: React.FC<{
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  value?: string;
  muted?: boolean;
  danger?: boolean;
  onPress: () => void;
  testID?: string;
}> = ({ icon, label, value, muted, danger, onPress, testID }) => {
  const color = danger ? Colors.dangerButton : muted ? Colors.textLight : Colors.textMedium;
  const labelColor = danger ? Colors.dangerButton : muted ? Colors.textLight : Colors.text;
  return (
    <Pressable
      style={({ hovered, pressed }: any) => [
        styles.row,
        (hovered || pressed) && styles.rowHovered,
      ]}
      onPress={onPress}
      testID={testID}
    >
      <Ionicons name={icon} size={20} color={color} />
      <Text style={[styles.rowLabel, { color: labelColor }]}>{label}</Text>
      {value ? <Text style={styles.rowValue}>{value}</Text> : null}
      <Ionicons name="chevron-forward-outline" size={16} color={Colors.border} />
    </Pressable>
  );
};

export const ProfileScreen: React.FC<Props> = ({ navigation }) => {
  const { t, i18n } = useTranslation();

  // Use contexts instead of local state
  const { user } = useUser();
  const { userClubs: myClubs, loading: loadingClubs, refreshClubs } = useUserClubs();

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [myListings, setMyListings] = useState<ClothingItem[]>([]);
  const [viewCounts, setViewCounts] = useState<Record<string, number>>({});
  const [soldCount, setSoldCount] = useState(0);
  const [loadingListings, setLoadingListings] = useState(true);
  const [activePanel, setActivePanel] = useState<ActivePanel>('none');
  const { isDesktop } = useResponsive();
  const isInitialMount = React.useRef(true);

  const loadMyListings = React.useCallback(async () => {
    if (!user) return;
    setLoadingListings(true);
    try {
      const listings = await itemsService.getUserItems(user.id);
      setMyListings(listings);
      // View counts and the sold total are nice-to-haves; load them separately
      // so a failure never blocks the listings themselves.
      itemsService
        .getMyListingViewCounts()
        .then(setViewCounts)
        .catch(() => setViewCounts({}));
      salesService
        .getMySalesCount()
        .then(setSoldCount)
        .catch(() => setSoldCount(0));
    } catch (error: any) {
      console.error('Failed to load listings:', error);
    } finally {
      setLoadingListings(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadMyListings();
    }
  }, [user, loadMyListings]);

  // Reload listings and clubs whenever the screen comes into focus
  // This ensures deleted items are removed from the list and club memberships are updated
  useFocusEffect(
    React.useCallback(() => {
      // Skip on initial mount since useEffect already loads listings
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }

      // Reload data when returning to this screen
      loadMyListings();
      refreshClubs();
    }, [loadMyListings, refreshClubs])
  );

  const handleLogoutPress = () => {
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutModal(false);
    try {
      await authService.signOut();
      // Navigation will be handled by auth state change
    } catch (error: any) {
      console.error('Failed to logout:', error);
    }
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  const toggleLanguage = () => i18n.changeLanguage(i18n.language === 'en' ? 'nl' : 'en');

  const memberSince = user ? new Date(user.created_at).toLocaleDateString() : '';
  const displayName = user
    ? user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.first_name || t('screens.profile.setupProfile')
    : '';
  const initial = user ? (user.first_name?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase()) : '';

  const openItem = (item: ClothingItem) => navigation.navigate('ItemDetails', { itemId: item.id });
  const sortedClubs = [...myClubs].sort((a, b) =>
    (a.club?.name?.toLowerCase() || '').localeCompare(b.club?.name?.toLowerCase() || '')
  );

  // Desktop edits inline in the content column; mobile pushes a screen.
  const goEditProfile = () => (isDesktop ? setActivePanel('editProfile') : navigation.navigate('EditProfile'));
  const goChangePassword = () => (isDesktop ? setActivePanel('changePassword') : navigation.navigate('ChangePassword'));
  const goDeleteAccount = () => (isDesktop ? setActivePanel('deleteAccount') : navigation.navigate('DeleteAccount'));

  // ── Profile card (polish round): identity, stats row, settings rows ──────
  const renderProfileCard = () => {
    if (!user) return null;
    const stats: Array<[number, string, (() => void) | undefined]> = [
      [myListings.length, t('screens.profile.statListings'), undefined],
      [soldCount, t('screens.profile.statSold'), undefined],
      [myClubs.length, t('screens.profile.statClubs'), () => navigation.navigate('Clubs')],
    ];
    return (
      <View style={styles.profileCard}>
        <View style={[styles.profileHeader, isDesktop && styles.profileHeaderDesktop]}>
          <View style={[styles.avatar, isDesktop && styles.avatarDesktop]}>
            {user.avatar_url ? (
              <Image
                source={{ uri: imageService.getCircularThumbnailUrl(user.avatar_url) }}
                style={isDesktop ? styles.avatarImageDesktop : styles.avatarImage}
              />
            ) : (
              <Text style={[styles.avatarText, isDesktop && styles.avatarTextDesktop]}>{initial}</Text>
            )}
          </View>
          <View style={[styles.profileInfo, isDesktop && styles.profileInfoDesktop]}>
            <Text style={[styles.profileName, isDesktop && styles.profileNameDesktop]} numberOfLines={1}>{displayName}</Text>
            <Text style={styles.profileEmail} numberOfLines={1}>{user.email}</Text>
            <Text style={[Typography.overline, styles.profileSince]}>
              {t('screens.profile.memberSince')} {memberSince}
            </Text>
          </View>
          <Button
            title={isDesktop ? t('screens.profile.editProfile') : t('screens.profile.edit')}
            icon="create-outline"
            variant={isDesktop ? 'primary' : 'ghost'}
            size="sm"
            onPress={goEditProfile}
            style={isDesktop ? styles.editButtonDesktop : undefined}
            testID="profile-edit"
          />
        </View>

        <View style={styles.statsRow}>
          {stats.map(([n, label, onPress], i) => (
            <TouchableOpacity
              key={label}
              style={[styles.statCell, i > 0 && styles.statCellBordered]}
              onPress={onPress}
              disabled={!onPress}
              activeOpacity={0.7}
            >
              <Text style={styles.statValue}>{n}</Text>
              <Text style={styles.statLabel}>{label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        <Text style={[Typography.overline, styles.overline]}>{t('screens.profile.settings')}</Text>
        <ProfileRow
          icon="language-outline"
          label={t('screens.profile.languageSection')}
          value={i18n.language.toUpperCase()}
          onPress={toggleLanguage}
          testID="profile-language"
        />
        <ProfileRow
          icon="key-outline"
          label={t('screens.profile.changePassword')}
          onPress={goChangePassword}
        />
        <ProfileRow
          icon="shield-checkmark-outline"
          label={t('screens.privacy.title')}
          onPress={() => navigation.navigate('PrivacyPolicy')}
        />
        {user.is_platform_admin && (
          <ProfileRow
            icon="construct-outline"
            label={t('screens.profile.adminDashboard')}
            onPress={() => navigation.navigate('AdminDashboard')}
          />
        )}
        <ProfileRow
          icon="log-out-outline"
          label={t('common.buttons.logout')}
          muted
          onPress={handleLogoutPress}
          testID="profile-logout"
        />
        <Text style={[Typography.overline, styles.overline]}>{t('screens.profile.dangerZone')}</Text>
        <ProfileRow
          icon="warning-outline"
          label={t('screens.deleteAccount.profileButton')}
          danger
          onPress={goDeleteAccount}
        />
      </View>
    );
  };

  const renderListings = () => (
    <View style={styles.section}>
      <SectionHeader
        mobile={!isDesktop}
        title={t('screens.profile.myListings')}
        count={myListings.length}
        action={isDesktop ? t('navigation.addListing') : t('screens.profile.addListing')}
        onAction={() => navigation.navigate('ItemForm')}
      />
      {loadingListings ? (
        <Text style={styles.loadingText}>{t('common.loading')}</Text>
      ) : myListings.length === 0 ? (
        <EmptyState
          title={t('screens.profile.noListings')}
          subtitle={t('screens.profile.noListingsSubtext')}
          actionLabel={t('screens.home.addFirstItem')}
          onAction={() => navigation.navigate('ItemForm')}
        />
      ) : (
        <View style={[styles.grid, isDesktop && styles.gridDesktop]}>
          {myListings.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              square={!isDesktop}
              viewCount={viewCounts[item.id] ?? 0}
              onPress={() => openItem(item)}
              style={isDesktop ? styles.itemCellDesktop : styles.gridItemMobile}
            />
          ))}
        </View>
      )}
    </View>
  );

  const renderClubs = () => (
    <View style={styles.section}>
      <SectionHeader
        title={t('screens.profile.myClubs')}
        kicker={t('screens.clubs.count', { count: myClubs.length })}
        action={t('screens.clubs.title')}
        onAction={() => navigation.navigate('Clubs')}
      />
      {loadingClubs ? (
        <Text style={styles.loadingText}>{t('common.loading')}</Text>
      ) : myClubs.length === 0 ? (
        <EmptyState
          icon="people-outline"
          title={t('screens.profile.noClubs')}
          subtitle={t('screens.profile.noClubsSubtext')}
          actionLabel={t('screens.clubDetail.joinClub')}
          onAction={() => navigation.navigate('Clubs')}
        />
      ) : (
        <View style={[styles.grid, styles.gridDesktop]}>
          {sortedClubs.map((userClub) =>
            userClub.club ? (
              <ClubCard
                key={userClub.club_id}
                club={userClub.club}
                joined
                onPress={() => navigation.navigate('ClubDetail', { clubId: userClub.club_id })}
                style={styles.gridItemDesktop}
              />
            ) : null
          )}
        </View>
      )}
    </View>
  );

  const renderDesktopPanel = () => {
    if (!user) return null;
    const close = () => setActivePanel('none');
    switch (activePanel) {
      case 'editProfile':
        return (
          <View style={styles.panel}>
            <PageTitle title={t('screens.profile.editProfileTitle')} onBack={close} />
            <EditProfileForm user={user} onSave={close} onCancel={close} showTitle={false} />
          </View>
        );
      case 'changePassword':
        return (
          <View style={styles.panel}>
            <PageTitle title={t('screens.changePassword.title')} onBack={close} />
            <ChangePasswordForm onDone={close} onCancel={close} showTitle={false} />
          </View>
        );
      case 'deleteAccount':
        return (
          <View style={styles.panel}>
            <PageTitle title={t('screens.deleteAccount.title')} onBack={close} />
            <DeleteAccountForm onCancel={close} />
          </View>
        );
      default:
        return null;
    }
  };

  // Don't show loading screen - render UI immediately for smooth transitions
  // Individual sections will show loading indicators if needed

  return (
    <ScreenLayout mobileTab="Profile">
      <View style={styles.container}>

      {/* Mobile header */}
      {!isDesktop && (
        <AppHeader
          navigation={navigation}
          currentScreen="Profile"
          title={t('screens.profile.title')}
        />
      )}

      <ScrollView style={styles.scrollContent} contentContainerStyle={styles.scrollContentContainer}>
        <View style={[styles.content, isDesktop ? styles.contentDesktop : styles.contentMobile]}>
          {user ? (
            isDesktop ? (
              <View style={styles.desktopLayout}>
                <View style={styles.sidebar}>{renderProfileCard()}</View>
                <View style={styles.main}>
                  {activePanel !== 'none' ? renderDesktopPanel() : (
                    <>
                      {renderListings()}
                      {renderClubs()}
                    </>
                  )}
                </View>
              </View>
            ) : (
              <>
                {renderProfileCard()}
                {renderListings()}
              </>
            )
          ) : (
            <View style={styles.profileCardLoading}>
              <Text style={styles.loadingText}>{t('common.loading')}</Text>
            </View>
          )}
        </View>
      </ScrollView>

      <ConfirmModal
        visible={showLogoutModal}
        title={t('common.buttons.logout')}
        message={t('screens.profile.logoutConfirmMessage')}
        confirmText={t('common.buttons.logout')}
        cancelText={t('common.buttons.cancel')}
        onConfirm={handleConfirmLogout}
        onCancel={handleCancelLogout}
        variant="danger"
        icon="log-out"
      />
      </View>
    </ScreenLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  scrollContent: {
    flex: 1,
  },
  scrollContentContainer: {
    flexGrow: 1,
  },
  content: {
    width: '100%',
  },
  contentMobile: {
    paddingTop: 14,
    paddingHorizontal: Spacing.base,
    paddingBottom: LAYOUT.tabBar.contentPadding,
  },
  contentDesktop: {
    paddingTop: Spacing['2xl'],
    paddingHorizontal: Spacing['3xl'],
    paddingBottom: Spacing['4xl'],
  },
  desktopLayout: {
    flexDirection: 'row',
    gap: Spacing['2xl'],
    alignItems: 'flex-start',
  },
  sidebar: {
    width: 320,
    flexShrink: 0,
  },
  main: {
    flex: 1,
    minWidth: 0,
  },
  panel: {
    maxWidth: 600,
  },

  // ── Profile card ─────────────────────────────────────────────────────────
  profileCard: {
    backgroundColor: Colors.white,
    borderRadius: Radii.card,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    overflow: 'hidden',
    ...Shadows.card,
  },
  profileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingTop: Spacing.lg,
    paddingHorizontal: Spacing.base,
    paddingBottom: 18,
  },
  profileHeaderDesktop: {
    flexDirection: 'column',
    gap: 10,
    paddingTop: 28,
    paddingHorizontal: Spacing.xl,
    paddingBottom: 22,
    backgroundColor: Colors.background,
  },
  avatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: Colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 3,
    borderColor: Colors.white,
    flexShrink: 0,
    ...Shadows.card,
  },
  avatarDesktop: {
    width: 88,
    height: 88,
    borderRadius: 44,
  },
  avatarImage: {
    width: 58,
    height: 58,
    borderRadius: 29,
  },
  avatarImageDesktop: {
    width: 82,
    height: 82,
    borderRadius: 41,
  },
  avatarText: {
    fontFamily: Fonts.display,
    fontSize: 26,
    color: Colors.textDark,
  },
  avatarTextDesktop: {
    fontSize: 34,
  },
  profileInfo: {
    flex: 1,
    minWidth: 0,
  },
  profileInfoDesktop: {
    flex: 0,
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  profileName: {
    fontFamily: Fonts.display,
    fontSize: 20,
    lineHeight: 24,
    color: Colors.textDark,
  },
  profileNameDesktop: {
    fontSize: 22,
    lineHeight: 27,
    textAlign: 'center',
  },
  profileEmail: {
    fontFamily: Fonts.regular,
    fontSize: 13,
    lineHeight: 18,
    color: Colors.textLight,
    marginTop: 2,
  },
  profileSince: {
    color: Colors.textMedium,
    marginTop: Spacing.sm,
  },
  editButtonDesktop: {
    marginTop: 6,
    height: 36,
    paddingHorizontal: Spacing.base,
  },
  statsRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: Colors.borderLight,
  },
  statCell: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: Spacing.sm,
  },
  statCellBordered: {
    borderLeftWidth: 1,
    borderLeftColor: Colors.borderLight,
  },
  statValue: {
    ...Typography.statValue,
    color: Colors.textDark,
  },
  statLabel: {
    fontFamily: Fonts.medium,
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: Colors.textLight,
    marginTop: 6,
  },
  overline: {
    color: Colors.textLight,
    paddingTop: 14,
    paddingHorizontal: Spacing.base,
    paddingBottom: 6,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
    paddingVertical: 14,
    paddingHorizontal: Spacing.base,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderLight,
    ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
  },
  rowHovered: {
    backgroundColor: Colors.background,
  },
  rowLabel: {
    flex: 1,
    fontFamily: Fonts.medium,
    fontSize: 15,
  },
  rowValue: {
    fontFamily: Fonts.medium,
    fontSize: 13,
    color: Colors.textLight,
  },

  // ── Sections ─────────────────────────────────────────────────────────────
  section: {
    marginTop: Spacing.xl,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.md,
  },
  gridDesktop: {
    gap: Spacing.lg,
  },
  gridItemMobile: {
    width: '48%',
    flexGrow: 1,
    maxWidth: '50%',
  },
  /** Naked listing cards: 4 to a row on desktop (gap 20), 2 on mobile */
  itemCellDesktop: {
    flexGrow: 1,
    minWidth: 0,
    width: '23%',
    maxWidth: '25%',
    ...(Platform.OS === 'web' && { flexBasis: 'calc(25% - 15px)', flexGrow: 0 } as any),
  },
  gridItemDesktop: {
    width: '31%',
    flexGrow: 1,
    maxWidth: '33%',
    minWidth: 220,
  },
  loadingText: {
    fontSize: 14,
    color: Colors.textMedium,
    textAlign: 'center',
    paddingVertical: 20,
  },
  profileCardLoading: {
    backgroundColor: Colors.white,
    borderRadius: Radii.card,
    padding: Spacing.base,
    marginBottom: Spacing.xl,
  },
});
